/**
 * Minimal inline SVG line graph for voice-network stats (no chart library
 * dependency). Handles missing data points gracefully.
 */

interface Props {
  /** Each item is indexed by `dataKey`; only numeric values are plotted. */
  data: readonly object[];
  dataKey: string;
  label: string;
  color: string;
  unit?: string;
  height?: number;
}

export const LineGraph: React.FC<Props> = ({
  data,
  dataKey,
  label,
  color,
  unit = "",
  height = 80,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="py-4 text-center text-xs text-gray-500 dark:text-gray-400">
        No data yet…
      </div>
    );
  }
  const values = data
    .map((d) => (d as Record<string, unknown>)[dataKey])
    .filter((v): v is number => typeof v === "number");
  if (values.length === 0) {
    return (
      <div className="py-4 text-center text-xs text-gray-500 dark:text-gray-400">
        Waiting for {label} data…
      </div>
    );
  }
  const width = 280;
  const padding = 20;
  const graphWidth = width - padding * 2;
  const graphHeight = height - padding * 2;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values
    .map((value, index) => {
      const x = padding + (index / (values.length - 1 || 1)) * graphWidth;
      const y = padding + graphHeight - ((value - min) / range) * graphHeight;
      return `${x},${y}`;
    })
    .join(" L ");
  const last = values[values.length - 1] ?? 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-600 dark:text-gray-400">
          {last.toFixed(dataKey === "ping" ? 0 : 1)} {unit}
        </span>
      </div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        <path d={`M ${points}`} fill="none" stroke={color} strokeWidth={2} />
      </svg>
    </div>
  );
};
